import { graphql } from 'react-apollo'
import { compose, withProps } from 'recompose'
import { contains } from 'ramda'

import flattenFirstTranslations from '../../utils/flattenFirstTranslations'
import sortByTitle from '../../utils/sortByTitle'

import withContentFilter from '../../hocs/withContentFilter'
import withErrorState from '../../hocs/withErrorState'
import withLoadingState from '../../hocs/withLoadingState'
import withLocalization from '../../hocs/withLocalization'
import withConnectionStatus from '../../hocs/withConnectionStatus'
import withRedirectWhenEmpty from '../../hocs/withRedirectWhenEmpty'

import vehicleWithRelations from '../../queries/vehicleWithRelations'

const filterByTopicIds = (topicIds, items) =>
  items.filter(({ topics }) => topics.some(({ id }) => contains(id, topicIds)))

const filterByCustomerBenefitIds = (ids, items) =>
  items.filter(({ customerBenefits }) =>
    customerBenefits.some(({ id }) => contains(id, ids))
  )

const filterByComponentIds = (componentIds, items) =>
  items.filter(({ components }) =>
    components.some(({ id }) => contains(id, componentIds))
  )

const flattenAndExtend = (type, vehicleSlug, items) => {
  return items.map(item => {
    const flattened = flattenFirstTranslations(item)
    const { slug, vehicles } = flattened
    return {
      ...flattened,
      path: `/vehicle/${vehicleSlug}/${type}/${slug}`,
      vehicleExclusive: vehicles && vehicles.length === 1
    }
  })
}

const filterListings = (
  contentFilter,
  customerBenefits,
  topics,
  components
) => {
  switch (contentFilter.type) {
    case 'customer-benefit': {
      const filteredTopics = filterByCustomerBenefitIds(
        [contentFilter.id],
        topics
      )
      const filteredTopicIds = filteredTopics.map(i => i.id)

      return {
        customerBenefits: customerBenefits.filter(
          ({ id }) => id === contentFilter.id
        ),
        topics: filteredTopics,
        components: filterByTopicIds(filteredTopicIds, components)
      }
    }
    case 'topic': {
      return {
        customerBenefits: filterByTopicIds(
          [contentFilter.id],
          customerBenefits
        ),
        topics: topics.filter(({ id }) => id === contentFilter.id),
        components: filterByTopicIds([contentFilter.id], components)
      }
    }
    case 'component': {
      const filteredTopics = filterByComponentIds([contentFilter.id], topics)
      const filteredTopicIds = filteredTopics.map(i => i.id)

      return {
        customerBenefits: filterByTopicIds(filteredTopicIds, customerBenefits),
        topics: filteredTopics,
        components: components.filter(({ id }) => id === contentFilter.id)
      }
    }
    default:
      return {
        customerBenefits,
        topics,
        components
      }
  }
}

const withSellerInformation = compose(
  withConnectionStatus,
  withLocalization,
  graphql(vehicleWithRelations, {
    options: ({ language, match, fetchPolicy, stage }) => ({
      variables: {
        vehicleSlug: match.params.vehicleSlug,
        lang: language.toLowerCase(),
        stage
      },
      fetchPolicy
    })
  }),
  withErrorState,
  withLoadingState('vehicle'),
  withRedirectWhenEmpty('vehicle'),
  withContentFilter,
  withProps(({ currentFilter, data, contentFilter, match }) => {
    const { vehicle } = data
    const vehicleSlug = match.params.vehicleSlug

    const vehicleContentFilter =
      contentFilter.vehicle === vehicleSlug ? contentFilter : {}

    const customerBenefits = flattenAndExtend(
      'customer-benefit',
      vehicleSlug,
      vehicle.customerBenefits
    )

    const topics = flattenAndExtend('topic', vehicleSlug, vehicle.topics)

    const components = flattenAndExtend(
      'component',
      vehicleSlug,
      vehicle.components
    )

    const filtered = filterListings(
      vehicleContentFilter,
      customerBenefits,
      topics,
      components
    )

    return {
      customerBenefits: sortByTitle(filtered.customerBenefits),
      topics: sortByTitle(filtered.topics),
      components: sortByTitle(filtered.components)
    }
  })
)

export default withSellerInformation



// WEBPACK FOOTER //
// ./src/screens/helpers/withSellerInformation.js